/**
 * /practice — the firm-wide practice-area hub.
 *
 * One long, deeply structured page: scope, real matters, document checklist,
 * working steps, cautions, handoff CTA and internal links for each of the ten
 * practice areas. Hand-authored content from src/data/practice/areas.ts, so the
 * route renders identically with zero AI credits.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import { absoluteAsset, absoluteUrl } from "@/config/sites";
import { canonicalAndHreflang } from "@/lib/hreflang";
import { PRACTICE_AREAS } from "@/data/practice/areas";
import { FirmCta } from "@/components/firm/FirmCta";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";
import { QaHubCta } from "@/components/QaHubCta";
import { LAWFIRM_QA } from "@/data/lawfirm/questions";
import teamAsset from "@/assets/team-group.png.asset.json";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  faqPageSchema,
} from "@/lib/schema";

const TITLE = "สายงานกฎหมายของสำนักงาน — คดีความ บริษัท ครอบครัว มรดก ที่ดิน";
const DESC =
  "ขอบเขตงานของสำนักงานกฎหมาย 10 สายงาน: คดีความทุกศาลและบังคับคดี บริษัทและสัญญา ครอบครัว มรดก ที่ดิน แรงงาน ภาษี ทรัพย์สินทางปัญญา PDPA และเอกสารระหว่างประเทศ พร้อมเอกสารที่ต้องใช้และขั้นตอนจริง";

const HUB_FAQ = [
  {
    question: "เรื่องของเราเกี่ยวข้องหลายสายงาน ต้องแยกจ้างหลายสำนวนหรือไม่?",
    answer:
      "ไม่ต้อง สำนักงานเปิดสำนวนเดียวและมีทนายผู้รับผิดชอบหลักหนึ่งท่านเป็นผู้ประสาน เช่น คดีมรดกที่มีที่ดินและทายาทอยู่ต่างประเทศ จะมีทั้งงานคดี งานที่ดิน และงานเอกสารระหว่างประเทศอยู่ในสำนวนเดียว เพื่อไม่ให้เอกสารซ้ำซ้อนและไม่ให้ข้อมูลตกหล่นระหว่างทาง",
  },
  {
    question: "ต้องเตรียมเอกสารครบก่อนเข้าพบทนายหรือไม่?",
    answer:
      "ไม่จำเป็นต้องครบตั้งแต่ครั้งแรก ส่งเท่าที่มีให้ทีมงานตรวจก่อนได้ ทนายจะแจ้งกลับว่าเอกสารส่วนใดยังขาด ส่วนใดต้องขอคัดสำเนาจากหน่วยงาน และส่วนใดต้องแปลหรือรับรองก่อนใช้ วิธีนี้ช่วยให้ท่านไม่ต้องเดินเอกสารซ้ำหลายรอบ",
  },
  {
    question: "สำนักงานรับเรื่องจากต่างจังหวัดหรือลูกความที่อยู่ต่างประเทศได้หรือไม่?",
    answer:
      "ได้ ทีมงานรับประเมินเรื่องเบื้องต้นจากภาพเอกสารที่ส่งมาทาง LINE หรืออีเมล และดำเนินการยื่นต่อศาลหรือหน่วยงานตามเขตอำนาจที่เกี่ยวข้อง ส่วนขั้นที่กฎหมายกำหนดให้ตัวความลงลายมือชื่อเองหรือมาแสดงตน ทีมงานจะแจ้งวิธีนัดหมายและทางเลือกที่ใช้ได้ เช่น การมอบอำนาจตามรูปแบบที่หน่วยงานปลายทางยอมรับ",
  },
  {
    question: "ค่าบริการแต่ละสายงานต่างกันอย่างไร?",
    answer:
      "ต่างกันตามลักษณะงานจริง เช่น ทุนทรัพย์ ศาลที่มีเขตอำนาจ จำนวนนัด ความซับซ้อนของเอกสารและจำนวนภาษาที่ต้องแปล จึงไม่มีอัตราเดียวสำหรับทุกเรื่อง ทีมงานจะประเมินและแจ้งค่าบริการเป็นลายลักษณ์อักษรก่อนเริ่มงาน ค่าธรรมเนียมศาลและค่าธรรมเนียมราชการเป็นอัตราของหน่วยงานและเรียกเก็บตามจริง",
  },
];

export const Route = createFileRoute("/practice/")({
  component: PracticeHub,
  loader: () => ({ h: (() => {
    const ogImage = absoluteAsset(teamAsset.url);
    return {
      meta: [
        { title: ctrTitle(TITLE) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:url", content: absoluteUrl("/practice", null, "th") },
        { property: "og:type", content: "website" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: "ทีมทนายของสำนักงาน — Thai Notary Law & Service" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: absoluteUrl("/practice", null, "th") },
        ...canonicalAndHreflang("/practice", null, "th").filter((l) => l.rel === "alternate"),
      ],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: "/practice", title: TITLE, description: DESC }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: absoluteUrl("/", null, "th") },
              { name: "สายงานกฎหมาย", url: absoluteUrl("/practice", null, "th") },
            ]),
            ...PRACTICE_AREAS.map((a) =>
              legalServiceSchema({
                name: a.name,
                description: a.tagline,
                url: `/practice#${a.slug}`,
                areaServed: "TH",
                serviceType: a.name,
              }),
            ),
            faqPageSchema(HUB_FAQ),
          ]),
        ),
      ],
    };
  })() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

function PracticeHub() {
  return (
    <>
      <header className="border-b border-border/60 text-[var(--primary-foreground)]" style={{ background: "var(--gradient-hero)" }}>
        <div className="mx-auto max-w-6xl px-6 py-14">
          <nav aria-label="เส้นทางหน้า" className="text-xs text-[var(--primary-foreground)]/70">
            <Link to="/" className="hover:underline">หน้าแรก</Link> <span aria-hidden>/</span> สายงานกฎหมาย
          </nav>
          <h1 className="mt-4 text-3xl font-bold leading-tight tracking-tight md:text-5xl">
            สายงานกฎหมายของสำนักงาน
          </h1>
          <p data-speakable className="answer-summary speakable-summary lead mt-5 max-w-3xl leading-relaxed text-[var(--primary-foreground)]/85">
            {SITE.legalName} รับงานกฎหมาย {PRACTICE_AREAS.length} สายงาน โดยทนายผู้ได้รับใบอนุญาตจากสภาทนายความในพระบรมราชูปถัมภ์
            แต่ละหัวข้อด้านล่างระบุขอบเขตงานที่สำนักงานทำจริง ตัวอย่างเรื่องที่ลูกความนำมา เอกสารที่ต้องเตรียมในรอบแรก
            ลำดับขั้นตอน และข้อควรระวังที่พบบ่อย เพื่อให้ท่านประเมินได้ก่อนตัดสินใจ
          </p>
          <ul className="mt-8 flex flex-wrap gap-2">
            {PRACTICE_AREAS.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/practice/$area"
                  params={{ area: a.slug }}
                  className="inline-block rounded-full border border-[var(--gold)]/40 px-3 py-1 text-xs font-medium hover:bg-[var(--primary-foreground)]/10"
                >
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6">
        {PRACTICE_AREAS.map((a) => (
          <section key={a.slug} id={a.slug} className="scroll-mt-24 border-b border-border/60 py-14">
            <h2 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
              <Link to="/practice/$area" params={{ area: a.slug }} className="hover:underline">
                {a.name}
              </Link>
            </h2>
            <p className="mt-2 text-sm font-semibold text-[var(--gold)]">{a.tagline}</p>
            <p className="mt-2 text-sm font-semibold">
              <Link to="/practice/$area" params={{ area: a.slug }} className="text-primary hover:underline">
                อ่านรายละเอียดสายงาน{a.name} — เอกสาร ขั้นตอน เปรียบเทียบทางเลือก และคำถามที่พบบ่อย →
              </Link>
            </p>
            <p data-speakable className="answer-summary mt-4 leading-relaxed text-muted-foreground">
              {a.intro}
            </p>

            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-base font-semibold text-foreground">ขอบเขตงานที่เราทำ</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {a.scope.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">ตัวอย่างเรื่องที่ลูกความนำมา</h3>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  {a.examples.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-border bg-card p-6">
              <h3 className="text-base font-semibold text-foreground">เอกสารที่ควรเตรียมในรอบแรก</h3>
              <ul className="mt-3 grid gap-2 text-sm leading-relaxed text-muted-foreground sm:grid-cols-2">
                {a.docs.map((s) => (
                  <li key={s} className="flex gap-2">
                    <span aria-hidden className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[var(--gold)]" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                หากเอกสารบางฉบับยังไม่มี ส่งเท่าที่มีให้ทีมงานตรวจก่อนได้ ทนายจะแจ้งวิธีขอคัดสำเนาจากหน่วยงานที่เกี่ยวข้อง
              </p>
            </div>

            <div className="mt-8">
              <h3 className="text-base font-semibold text-foreground">ขั้นตอนการทำงาน</h3>
              <ol className="mt-3 space-y-3">
                {a.steps.map((s, i) => (
                  <li key={s} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">
                      {i + 1}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-8">
              <h3 className="text-base font-semibold text-foreground">ข้อควรระวัง</h3>
              <div className="mt-3 space-y-3">
                {a.cautions.map((c) => (
                  <div key={c.title} className="rounded-lg border-l-4 border-[var(--gold)] bg-muted/50 p-4">
                    <p className="text-sm font-semibold text-foreground">{c.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 rounded-xl border border-[var(--gold)]/40 bg-muted/40 p-6">
              <h3 className="text-base font-semibold text-foreground">ให้สำนักงานดูแลทั้งเรื่อง</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.handoff}</p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm font-semibold">
                <Link to="/contact" className="text-primary hover:underline">
                  ปรึกษาทนายเรื่องนี้ →
                </Link>
                {a.links.map((l) => (
                  <Link key={l.to} to={l.to} className="text-primary hover:underline">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}
      </div>

      <section aria-labelledby="practice-choose" className="mx-auto max-w-4xl px-6 py-16">
        <h2 id="practice-choose" className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          ก่อนจ้างทนาย: 5 เรื่องที่ควรตรวจด้วยตัวเอง
        </h2>
        <p data-speakable className="answer-summary mt-4 leading-relaxed text-muted-foreground">
          ผู้ที่จะว่าความในศาลแทนท่านต้องมีใบอนุญาตทนายความที่ยังมีผลตามพระราชบัญญัติทนายความ พ.ศ. 2528
          ท่านตรวจสอบชื่อและเลขใบอนุญาตได้เองจากเว็บไซต์ของสภาทนายความ ก่อนลงนามในใบแต่งทนายความหรือสัญญาจ้างว่าความ
        </p>
        <ol className="mt-8 space-y-4">
          {[
            ["ขอดูใบอนุญาตทนายความ", "ถามชื่อ-นามสกุลและเลขใบอนุญาตของทนายผู้รับผิดชอบ แล้วตรวจกับสภาทนายความว่าใบอนุญาตยังมีผลอยู่"],
            ["อ่านขอบเขตงานในสัญญาจ้าง", "สัญญาควรระบุว่าครอบคลุมศาลชั้นใด รวมงานอุทธรณ์ ฎีกา หรือบังคับคดีหรือไม่ เพราะแต่ละชั้นเป็นงานคนละส่วน"],
            ["แยกค่าวิชาชีพกับค่าธรรมเนียมราชการ", "ค่าธรรมเนียมศาล ค่านำหมาย และค่าธรรมเนียมหน่วยงานเรียกเก็บตามอัตราของหน่วยงาน ควรได้รับใบเสร็จของหน่วยงานนั้นทุกครั้ง"],
            ["ระวังผู้ที่รับรองผลคดี", "ผลคดีขึ้นกับพยานหลักฐานและดุลพินิจของศาล ทนายที่ปฏิบัติตามมรรยาทวิชาชีพจะอธิบายความเสี่ยงและทางเลือก ไม่รับรองว่าชนะ"],
            ["ตรวจอายุความของเรื่อง", "หลายเรื่องมีอายุความสั้น เช่น การเรียกค่าเสียหายจากละเมิดโดยทั่วไปต้องฟ้องภายใน 1 ปีนับแต่รู้ถึงการละเมิดและรู้ตัวผู้ต้องรับผิด (ป.พ.พ. มาตรา 448) จึงควรปรึกษาแต่เนิ่น ๆ"],
          ].map(([t, b], i) => (
            <li key={t} className="flex gap-3 rounded-xl border border-border bg-card p-5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-xs font-bold text-[var(--gold)]">{i + 1}</span>
              <div>
                <p className="font-semibold text-foreground">{t}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{b}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="practice-faq" className="mx-auto max-w-4xl px-6 py-16">
        <h2 id="practice-faq" className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          คำถามที่พบบ่อยเรื่องการจ้างสำนักงานกฎหมาย
        </h2>
        <dl className="mt-8 space-y-6">
          {HUB_FAQ.map((f) => (
            <div key={f.question} className="rounded-xl border border-border bg-card p-6">
              <dt className="text-base font-semibold text-foreground">{f.question}</dt>
              <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.answer}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="border-t border-border/60">
        <QaHubCta base="/practice" label="การจ้างสำนักงานกฎหมายและทนายความ" qa={LAWFIRM_QA} />
      </div>

      <div className="mx-auto max-w-4xl px-6 pb-16">
        <OfficialServiceSources theme="law" />
        <p className="mt-6 text-xs text-muted-foreground">
          ปรับปรุงเนื้อหาล่าสุด: 7 ตุลาคม 2569 · ข้อมูลในหน้านี้เป็นความรู้ทั่วไป ไม่ใช่คำแนะนำทางกฎหมายสำหรับเรื่องเฉพาะของท่าน
        </p>
      </div>

      <FirmCta heading="ปรึกษาทนายที่รับผิดชอบสายงานของท่าน" />
    </>
  );
}
